import React from 'react';
import Stack from '@mui/material/Stack/Stack';
import Box from '@mui/material/Box/Box';
import Typography from '@mui/material/Typography/Typography';
import Chip from '@mui/material/Chip/Chip';
import CheckIcon from '@mui/icons-material/Check';
import { alpha } from '@mui/material/styles';
import { formatPrice, Pricing } from '../../services/billing.service';
import { LOOK } from '../../theme';

const Features = ({ items }: { items: string[] }) => (
    <Stack component="ul" spacing={0} sx={{ listStyle: 'none', p: 0, m: 0 }}>
        {items.map(item => (
            <Stack component="li" key={item} direction="row" spacing={1.5} alignItems="flex-start" sx={{ py: 1.1, borderTop: `1px solid ${alpha(LOOK.ink, 0.12)}` }}>
                <CheckIcon fontSize="small" sx={{ mt: '3px', color: LOOK.ink }} />
                <Typography>{item}</Typography>
            </Stack>
        ))}
    </Stack>
);

const Plan = ({ name, price, per, lead, items, current, action, highlight }: {
    name: string, price: string, per?: string, lead: string, items: string[], current: boolean, action?: React.ReactNode, highlight?: boolean
}) => (
    <Box sx={{
        flex: 1,
        borderRadius: '28px',
        p: { xs: 3, md: 4.5 },
        // Pro sits on the kraft graph paper; Free on plain paper
        background: highlight ? `${LOOK.grain}, ${LOOK.heroPanel}` : LOOK.paper,
        border: highlight ? 'none' : `1px solid ${LOOK.line}`,
        boxShadow: highlight ? `0 30px 60px -30px ${alpha(LOOK.terminal, 0.35)}` : 'none'
    }}>
        <Stack spacing={2.5}>
            <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h3" component="h2">{name}</Typography>
                {current && <Chip size="small" variant="outlined" label="This event" />}
            </Stack>
            <Box>
                <Typography variant="h1" component="p" sx={{ fontSize: { xs: '3.5rem', md: '4.5rem' }, lineHeight: 1 }}>{price}</Typography>
                {per && <Typography color="text.secondary" sx={{ mt: 0.5 }}>{per}</Typography>}
            </Box>
            <Typography sx={{ fontSize: '1.15rem' }}>{lead}</Typography>
            <Features items={items} />
            {action}
        </Stack>
    </Box>
);

/**
 * Free next to Pro event, with Pro's call to action (an upgrade button, or a note) passed in. Used on the pricing page and
 * the upgrade page so both always say the same thing.
 */
export const PlanCards = ({ pricing, current, proAction }: {
    pricing: Pricing,
    // the event's plan, to mark it (omitted on the public pricing page)
    current?: 'free' | 'pro',
    proAction?: React.ReactNode
}) => (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 2, md: 3 }} alignItems="stretch">
        <Plan name="Free" price={formatPrice(0, pricing.currency)} lead="Run a whole hackathon, start to finish."
            items={pricing.free} current={current === 'free'} />
        <Plan name="Pro event" price={formatPrice(pricing.proEventPrice, pricing.currency)} per="per event, one-time"
            lead="Everything in Free, looking like your company." items={pricing.pro} current={current === 'pro'}
            action={proAction} highlight />
    </Stack>
);
